import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Handshake, Calendar, Users, Building2, CheckCircle, Clock, Info, FileText, BarChart3, ExternalLink, CheckSquare, FileImage, AlertCircle, XCircle, Download, MessageCircle, Instagram, Youtube, Link as LinkIcon, Loader, User } from 'lucide-react';
import type { Collab } from '../lib/supabase';
import InfluencerAvatar from '../components/InfluencerAvatar';

const encodeSupabaseUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    const encodedParts = pathParts.map((part, index) =>
      index === pathParts.length - 1 ? encodeURIComponent(part) : part
    );
    urlObj.pathname = encodedParts.join('/');
    return urlObj.toString();
  } catch {
    return url;
  }
};

const sanitizeFileName = (fileName: string): string => {
  return fileName
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_');
};

export default function PublicCampaignView() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<Collab | null>(null);
  const [timelineItems, setTimelineItems] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [stats, setStats] = useState<any[]>([]);
  const [filteredTimelineItems, setFilteredTimelineItems] = useState<any[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<any[]>([]); 
  const [filteredStats, setFilteredStats] = useState<any[]>([]); 
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDebugMode, setIsDebugMode] = useState(false);
  const [activeTab, setActiveTab] = useState('timeline');
  const [agencyName, setAgencyName] = useState('');
  const [currentInfluencerName, setCurrentInfluencerName] = useState<string | null>(null);
  const [currentInfluencer, setCurrentInfluencer] = useState<any | null>(null);
  const [currentInfluencerId, setCurrentInfluencerId] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>({});
  const [influencerProfiles, setInfluencerProfiles] = useState<any[]>([]);

  useEffect(() => {
    if (token) {
      loadCampaignData();
    }
  }, [token]);

  // Effet pour filtrer les données quand l'influenceur change
  useEffect(() => {
    if (timelineItems.length > 0 || documents.length > 0 || stats.length > 0) {
      console.log(`Filtrage des données pour l'influenceur ID: ${currentInfluencerId || 'tous'}`);
      
      // Fonction de filtrage améliorée
      const filterByInfluencer = (item: any) => {
        // Si pas d'influenceur spécifié, montrer tout
        if (!currentInfluencerId) return true;
        
        // Si l'item n'a pas d'influencerId ou est vide/null, c'est un élément global
        const itemInfluencerId = item.influencerId;
        
        if (itemInfluencerId === undefined || 
            itemInfluencerId === null || 
            itemInfluencerId === '') {
          return true; // Élément global visible par tous
        }
        
        // Sinon, vérifier si l'ID correspond exactement
        return itemInfluencerId === currentInfluencerId;
      };
      
      // Filtrer les éléments de timeline
      const filteredTimeline = timelineItems.filter(filterByInfluencer);
      setFilteredTimelineItems(filteredTimeline);
      
      // Filtrer les documents
      const filteredDocs = documents.filter(filterByInfluencer);
      setFilteredDocuments(filteredDocs);
      
      // Filtrer les statistiques
      const filteredStatistics = stats.filter(filterByInfluencer);
      setFilteredStats(filteredStatistics);
      
      console.log(`Filtrage terminé: ${filteredTimeline.length}/${timelineItems.length} timeline, ${filteredDocs.length}/${documents.length} documents, ${filteredStatistics.length}/${stats.length} stats`);
    } else {
      // Si pas de données, initialiser avec des tableaux vides
      setFilteredTimelineItems([]);
      setFilteredDocuments([]);
      setFilteredStats([]);
    }
  }, [currentInfluencerId, timelineItems, documents, stats]);

  const loadCampaignData = async () => {
    try {
      console.log('Chargement des données pour le token:', token);
      
      // Check if we're coming from an influencer view
      const urlParams = new URLSearchParams(window.location.search);
      const influencerName = urlParams.get('influencer');
      const influencerId = urlParams.get('influencerId');

      if (influencerName && influencerName.trim()) {
        const normalizedInfluencerName = influencerName.trim();
        console.log(`Influenceur spécifié dans l'URL: "${normalizedInfluencerName}"`);
        setCurrentInfluencerName(normalizedInfluencerName);
      }

      if (influencerId && influencerId.trim()) {
        console.log(`ID d'influenceur spécifié dans l'URL: "${influencerId}"`);
        setCurrentInfluencerId(influencerId);
      }
      
      setIsLoading(true);
      setError(null); 

      // Vérifier si c'est un token temporaire
      const isTemporaryToken = token?.startsWith('temp-');

      let campaignData: Collab | null = null;

      const { supabase } = await import('../lib/supabase');

      if (isTemporaryToken) {
        // Pour un token temporaire, extraire l'ID partiel
        const partialId = token.substring(5); // Enlever 'temp-'

        // Chercher une collaboration dont l'ID commence par le partialId
        const { data: collabs, error } = await supabase
          .from('collabs')
          .select('*')
          .ilike('id', `${partialId}%`)
          .limit(1);

        if (!error && collabs && collabs.length > 0) {
          campaignData = {
            ...collabs[0],
            // Forcer ces valeurs pour un accès temporaire
            link_active: true,
            link_token: token
          } as Collab;
        }
      } else {
        // Rechercher la collaboration par token normal
        const { data: collabs, error } = await supabase
          .from('collabs')
          .select('*')
          .eq('link_token', token)
          .limit(1);

        if (!error && collabs && collabs.length > 0) {
          campaignData = collabs[0] as Collab;
        }
      }
      
      if (!campaignData) {
        console.error('Aucune collaboration trouvée avec ce token:', token);
        setError('Cette campagne n\'existe pas ou le lien n\'est plus actif.');
        setDebugInfo({
          token,
          error: 'Aucune collaboration trouvée',
          timestamp: new Date().toISOString()
        });
        setIsLoading(false);
        return;
      }
            
      console.log('Campagne trouvée:', campaignData);

      // Enrichir les données des influenceurs avec leurs profils complets
      if (campaignData.influencers && campaignData.influencers.length > 0) {
        // D'abord, normaliser les noms (snake_case -> camelCase)
        campaignData.influencers = campaignData.influencers.map(inf => ({
          ...inf,
          fullName: inf.fullName || inf.full_name,
          profile_image_url: inf.profile_image_url
        }));

        const influencerIds = campaignData.influencers
          .map(inf => inf.id)
          .filter(id => id && id.trim() !== '');

        if (influencerIds.length > 0) {
          const { data: influencerProfiles, error: influencerError } = await supabase
            .from('influencers')
            .select('id, display_name, full_name, profile_image_url, username, followers_count')
            .in('id', influencerIds);

          if (!influencerError && influencerProfiles) {
            // Enrichir chaque influenceur avec son profil complet
            campaignData.influencers = campaignData.influencers.map(inf => {
              const profile = influencerProfiles.find(p => p.id === inf.id);
              if (profile) {
                return {
                  ...inf,
                  fullName: inf.fullName || profile.full_name || profile.display_name,
                  profile_image_url: inf.profile_image_url || profile.profile_image_url,
                  instagramUsername: profile.username,
                  followers: profile.followers_count
                };
              }
              return inf;
            });
            console.log('Profils des influenceurs enrichis:', influencerProfiles.length);
          }
        }
      }

      setCampaign(campaignData);

      // Trouver l'influenceur correspondant si un ID ou un nom est fourni
      if (campaignData.influencers && campaignData.influencers.length > 0) {
        // Priorité à l'ID si fourni
        if (influencerId) {
          const matchingInfluencer = campaignData.influencers.find(inf => inf.id === influencerId);

          if (matchingInfluencer) {
            console.log('Influenceur trouvé par ID:', matchingInfluencer);
            setCurrentInfluencer(matchingInfluencer);
            setCurrentInfluencerId(matchingInfluencer.id || null);
          } else {
            console.warn(`Aucun influenceur trouvé avec l'ID: ${influencerId}`);
          }
        }
        // Sinon chercher par nom
        else if (influencerName) {
          const normalizedInfluencerName = influencerName.trim().toLowerCase();
          console.log(`Recherche de l'influenceur: "${normalizedInfluencerName}"`);
          console.log('Influenceurs disponibles:', campaignData.influencers.map(inf => ({
            name: inf.fullName,
            id: inf.id
          })));

          const matchingInfluencer = campaignData.influencers.find(inf =>
            (inf.fullName || '').toLowerCase().trim() === normalizedInfluencerName
          );

          if (matchingInfluencer) {
            console.log('Influenceur trouvé:', matchingInfluencer);
            setCurrentInfluencer(matchingInfluencer);
            setCurrentInfluencerId(matchingInfluencer.id || null);
            console.log('ID de l\'influenceur trouvé:', matchingInfluencer.id);

            // Si l'ID est une chaîne vide, considérer comme null
            if (matchingInfluencer.id === '') {
              console.warn('L\'ID de l\'influenceur est une chaîne vide, considéré comme null');
              setCurrentInfluencerId(null);
            }
          } else {
            console.warn(`Aucun influenceur trouvé avec le nom: ${influencerName}`);
          }
        }
      }
      
      // Récupérer le nom de l'agence
      if (campaignData.agency_id) {
        try {
          const { data: agencyData, error: agencyError } = await supabase
            .from('agencies')
            .select('name')
            .eq('id', campaignData.agency_id)
            .maybeSingle();

          if (!agencyError && agencyData) {
            setAgencyName(agencyData.name || campaignData.agency_name || '');
          } else if (campaignData.agency_name) {
            setAgencyName(campaignData.agency_name);
          }
        } catch (agencyError) {
          console.error('Erreur lors de la récupération de l\'agence:', agencyError);
          if (campaignData.agency_name) {
            setAgencyName(campaignData.agency_name);
          }
        }
      }

      // Récupérer les éléments de timeline depuis Supabase
      try {
        if (campaignData.id) {
          const { data: timelineData, error: timelineError } = await supabase
            .from('timeline_items')
            .select('*')
            .eq('collab_id', campaignData.id)
            .order('order_index', { ascending: true });

          if (!timelineError && timelineData) {
            // Normaliser les données de timeline
            for (const item of timelineData) {
              // Normaliser: considérer les chaînes vides comme null
              if (item.influencer_id === '') {
                item.influencer_id = null;
              }

              // Convertir snake_case en camelCase
              item.influencerId = item.influencer_id;
              item.stepName = item.step_name;
              item.collabId = item.collab_id;
              item.influencerName = item.influencer_name;
              item.createdAt = item.created_at;
              item.updatedAt = item.updated_at;

              // Ajouter le nom de l'influenceur si possible
              if (item.influencer_id && campaignData.influencers) {
                const influencer = campaignData.influencers.find(inf => inf.id === item.influencer_id);
                if (influencer) {
                  item.influencer_name = influencer.fullName;
                  item.influencerName = influencer.fullName;
                }
              }
            }

            setTimelineItems(timelineData);
            setFilteredTimelineItems(timelineData);

            console.log('Éléments de timeline chargés:', timelineData.length,
                       'avec influencer_id:', timelineData.filter(item => item.influencer_id).length);
          }
        }
      } catch (timelineError) {
        console.error('Erreur lors de la récupération des éléments de timeline:', timelineError);
      }
      
      // Récupérer les documents (uniquement ceux marqués comme visibles pour la marque) depuis Supabase
      try {
        if (campaignData.id) {
          const { data: documentsData, error: documentsError } = await supabase
            .from('collab_documents')
            .select('*')
            .eq('collab_id', campaignData.id)
            .eq('visible_for_brand', true);

          if (!documentsError && documentsData) {
            // Normaliser les données de documents
            for (const doc of documentsData) {
              // Normaliser: considérer les chaînes vides comme null
              if (doc.influencer_id === '') {
                doc.influencer_id = null;
              }

              // Convertir snake_case en camelCase
              doc.influencerId = doc.influencer_id;
              doc.collabId = doc.collab_id;
              doc.influencerName = doc.influencer_name;
              doc.fileUrl = doc.file_url;
              doc.fileName = doc.file_name;
              doc.fileSize = doc.file_size;
              doc.visibleForBrand = doc.visible_for_brand;
              doc.createdAt = doc.created_at;
              doc.updatedAt = doc.updated_at;

              // Ajouter le nom de l'influenceur si possible
              if (doc.influencer_id && campaignData.influencers) {
                const influencer = campaignData.influencers.find(inf => inf.id === doc.influencer_id);
                if (influencer) {
                  doc.influencer_name = influencer.fullName;
                  doc.influencerName = influencer.fullName;
                }
              }
            }

            setDocuments(documentsData);
            setFilteredDocuments(documentsData);
            console.log('Documents chargés:', documentsData.length);
          }
        }
      } catch (documentsError) {
        console.error('Erreur lors de la récupération des documents:', documentsError);
      }

      // Récupérer les statistiques depuis Supabase
      try {
        if (campaignData.id) {
          const { data: statsData, error: statsError } = await supabase
            .from('collab_stats')
            .select('*')
            .eq('collab_id', campaignData.id);

          if (!statsError && statsData) {
            // Normaliser les données de statistiques
            for (const stat of statsData) {
              // Normaliser: considérer les chaînes vides comme null
              if (stat.influencer_id === '') {
                stat.influencer_id = null;
              }

              // Ajouter influencerId en camelCase pour le filtrage
              stat.influencerId = stat.influencer_id;

              // Ajouter le nom de l'influenceur si possible
              if (stat.influencer_id && campaignData.influencers) {
                const influencer = campaignData.influencers.find(inf => inf.id === stat.influencer_id);
                if (influencer) {
                  stat.influencer_name = influencer.fullName;
                }
              }
            }

            setStats(statsData);
            setFilteredStats(statsData);
            console.log('Statistiques chargées:', statsData.length);
          }
        }
      } catch (statsError) {
        console.error('Erreur lors de la récupération des statistiques:', statsError);
      }
      
      // Ajouter un bouton de debug caché (triple-clic sur le footer)
      setTimeout(() => {
        const footer = document.querySelector('footer');
        if (footer) footer.addEventListener('click', (e) => e.detail === 3 && setIsDebugMode(prev => !prev));
      }, 1000);
      
      setDebugInfo({
        token,
        campaignId: campaignData.id,
        campaignName: campaignData.name,
        linkActive: campaignData.linkActive,
        timelineCount: timelineItems.length,
        documentsCount: documents.length,
        currentInfluencerId,
        timestamp: new Date().toISOString()
      });
      
    } catch (error) {
      console.error('Error loading campaign data:', error);
      setError('Une erreur est survenue lors du chargement des données.');
      setDebugInfo({
        token,
        error: error instanceof Error ? error.message : 'Erreur inconnue',
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction utilitaire pour convertir une valeur en nombre
  const toNumber = (value: string | number | undefined): number => {
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    if (typeof value === 'number') return value;
    return 0;
  };

  // Fonction pour obtenir l'icône d'une plateforme sociale
  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'Instagram':
        return <Instagram className="w-4 h-4" />;
      case 'YouTube':
        return <Youtube className="w-4 h-4" />;
      case 'TikTok':
      case 'Twitch':
      default:
        return <MessageCircle className="w-4 h-4" />;
    }
  };

  const formatDate = (date: Date | { seconds: number } | string | undefined) => {
    if (!date) return 'Date non définie';
    
    if (typeof date === 'string') {
      return new Date(date).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    if (typeof date === 'object' && date !== null && 'seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '0 €';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(num);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getStatusDot = (status: string) => {
    switch (status) {
      case 'done': return 'bg-emerald-500';
      case 'in_progress': return 'bg-[#D4AF37]';
      case 'late': return 'bg-red-500';
      default: return 'bg-[#3A3A3A]';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'done': return 'Terminé';
      case 'in_progress': return 'En cours';
      case 'late': return 'En retard';
      default: return 'À faire';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'done': return 'bg-emerald-950/60 text-emerald-400 border border-emerald-800/40';
      case 'in_progress': return 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30';
      case 'late': return 'bg-red-950/60 text-red-400 border border-red-800/40';
      default: return 'bg-[#2A2A2A] text-[#888888] border border-[#3A3A3A]';
    }
  };

  const getCampaignStatus = (campaign: Collab) => {
    if (campaign.invoice_sent || campaign.payment_received) return { label: 'Terminée', badge: 'bg-emerald-950/60 text-emerald-400 border border-emerald-800/40' };
    if (campaign.quote_sent || campaign.contract_signed) return { label: 'En cours', badge: 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30' };
    return { label: 'En attente', badge: 'bg-[#2A2A2A] text-[#888888] border border-[#3A3A3A]' };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl p-8 text-center">
          <h1 className="text-xl font-bold text-[#FAFAFA] mb-2">Lien non valide</h1>
          <p className="text-[#888888] text-sm mb-6">
            {error || "Ce lien de suivi n'est plus actif ou n'existe pas."}
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-5 py-2 bg-[#FAFAFA] text-[#0A0A0A] rounded-xl text-sm font-semibold hover:bg-[#D4AF37] transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  const campaignStatus = getCampaignStatus(campaign);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .fade-up { animation: fadeUp 0.4s ease forwards; }
        .fade-up-1 { animation-delay: 0.05s; opacity: 0; }
        .fade-up-2 { animation-delay: 0.12s; opacity: 0; }
        .fade-up-3 { animation-delay: 0.2s; opacity: 0; }
        .fade-up-4 { animation-delay: 0.28s; opacity: 0; }
      `}</style>

      {/* Header */}
      <header className="border-b border-[#1E1E1E] bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-base font-bold text-[#FAFAFA]">{campaign.name}</h1>
            <p className="text-xs text-[#888888] mt-0.5">Suivi de campagne · Campaly</p>
          </div>
          <span className="text-xs text-[#555555]">
            Mis à jour {formatDate(campaign.updatedAt)}
          </span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-5">

        {/* Info cards */}
        <div className="fade-up fade-up-1 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Marque', value: campaign.brand },
            { label: 'Date', value: formatDate(campaign.signed_date) },
            { label: 'Chef de projet', value: campaign.manager },
          ].map(({ label, value }) => (
            <div key={label} className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl p-5">
              <p className="text-xs text-[#555555] uppercase tracking-wider mb-2">{label}</p>
              <p className="text-base font-semibold text-[#FAFAFA]">{value || '—'}</p>
            </div>
          ))}
        </div>

        {/* Tabs + content */}
        <div className="fade-up fade-up-2 bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl overflow-hidden">
          <div className="flex border-b border-[#2A2A2A]">
            {[
              { id: 'timeline', label: 'Timeline' },
              { id: 'influencers', label: 'Influenceurs' },
              { id: 'documents', label: 'Documents' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-3.5 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'border-[#D4AF37] text-[#D4AF37]'
                    : 'border-transparent text-[#888888] hover:text-[#FAFAFA]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6">

            {/* Timeline Tab */}
            {activeTab === 'timeline' && (
              <div className="space-y-6">
                <p className="text-xs text-[#555555] uppercase tracking-wider">
                  {currentInfluencerName ? 'Vos étapes' : 'Avancement de la campagne'}
                </p>

                {isDebugMode && (
                  <div className="p-3 bg-[#111111] border border-[#2A2A2A] rounded-lg text-xs font-mono text-[#888888]">
                    <p>Influenceur: {currentInfluencerName || 'Aucun'} — Timeline: {filteredTimelineItems.length}/{timelineItems.length}</p>
                  </div>
                )}

                {filteredTimelineItems.length === 0 ? (
                  <p className="text-[#555555] text-sm text-center py-10">Aucune étape définie pour cette campagne.</p>
                ) : (
                  <div className="space-y-8">
                    {(() => {
                      const groupedItems = filteredTimelineItems.reduce((acc, item) => {
                        const key = item.influencerId || 'common';
                        if (!acc[key]) acc[key] = [];
                        acc[key].push(item);
                        return acc;
                      }, {} as Record<string, typeof filteredTimelineItems>);

                      const commonItems = groupedItems['common'] || [];
                      const influencerGroups = Object.entries(groupedItems).filter(([key]) => key !== 'common');

                      const renderItems = (items: typeof filteredTimelineItems) => (
                        <div className="space-y-2">
                          {items.map((item) => (
                            <div key={item.id} className="flex items-center gap-4 p-4 bg-[#111111] border border-[#2A2A2A] rounded-xl hover:border-[#3A3A3A] transition-colors">
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusDot(item.status)}`} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[#FAFAFA]">{item.stepName}</p>
                                {item.deadline && (
                                  <p className="text-xs text-[#555555] mt-0.5">Échéance : {formatDate(item.deadline)}</p>
                                )}
                              </div>
                              <span className={`text-xs px-2.5 py-1 rounded-lg font-medium flex-shrink-0 ${getStatusBadge(item.status)}`}>
                                {getStatusText(item.status)}
                              </span>
                            </div>
                          ))}
                        </div>
                      );

                      return (
                        <>
                          {commonItems.length > 0 && (
                            <div>
                              {influencerGroups.length > 0 && (
                                <p className="text-xs text-[#555555] uppercase tracking-wider mb-3">Étapes communes</p>
                              )}
                              {renderItems(commonItems)}
                            </div>
                          )}
                          {influencerGroups.map(([influencerId, items]) => {
                            const influencer = campaign.influencers?.find((inf: any) => inf.id === influencerId);
                            const infName = influencer?.fullName || influencer?.full_name || items[0]?.influencerName || 'Influenceur';
                            const profileImageUrl = influencer?.profileImageUrl || influencer?.profile_image_url || null;
                            return (
                              <div key={influencerId}>
                                <div className="flex items-center gap-2 mb-3">
                                  {influencer && <InfluencerAvatar profileImageUrl={profileImageUrl} name={infName} size="sm" />}
                                  <p className="text-xs text-[#888888] uppercase tracking-wider font-medium">{infName}</p>
                                </div>
                                {renderItems(items)}
                              </div>
                            );
                          })}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Influencers Tab */}
            {activeTab === 'influencers' && (
              <div className="space-y-4">
                <p className="text-xs text-[#555555] uppercase tracking-wider">Influenceurs sélectionnés</p>
                {campaign.influencers && campaign.influencers.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {campaign.influencers
                      .filter(inf => !currentInfluencerName || inf.fullName === currentInfluencerName)
                      .map((influencer, index) => (
                        <div key={index} className="bg-[#111111] border border-[#2A2A2A] rounded-xl p-5">
                          <div className="flex items-center gap-3 mb-4">
                            <InfluencerAvatar profileImageUrl={influencer.profile_image_url || null} name={influencer.fullName} size="md" />
                            <p className="font-semibold text-[#FAFAFA]">{influencer.fullName}</p>
                          </div>
                          {influencer.deliverables && influencer.deliverables.length > 0 && (
                            <div>
                              <p className="text-xs text-[#555555] uppercase tracking-wider mb-3">Livrables</p>
                              <div className="space-y-2">
                                {influencer.deliverables.map((deliverable: any, idx: number) => (
                                  <div key={idx} className="flex items-center justify-between py-2 border-b border-[#2A2A2A] last:border-0">
                                    <span className="text-sm text-[#AAAAAA]">{(deliverable.type || '').replace(/_/g, ' ')}</span>
                                    <span className="text-sm font-semibold text-[#D4AF37]">{formatCurrency(deliverable.price || '0')}</span>
                                  </div>
                                ))}
                              </div>
                              <div className="mt-3 flex justify-between items-center pt-2">
                                <span className="text-xs text-[#555555]">Total</span>
                                <span className="font-bold text-[#D4AF37]">{formatCurrency(influencer.rate || '0')}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-[#555555] text-sm text-center py-10">Aucun influenceur associé.</p>
                )}
                {currentInfluencerName && campaign.influencers && campaign.influencers.length > 1 && (
                  <p className="text-xs text-[#555555] text-center">
                    Vous visualisez uniquement vos informations. {campaign.influencers.length - 1} autre(s) créateur(s) participent à cette collaboration.
                  </p>
                )}
              </div>
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
              <div className="space-y-4">
                <p className="text-xs text-[#555555] uppercase tracking-wider">
                  {currentInfluencerName ? 'Vos documents' : 'Documents et preview'}
                </p>
                {isDebugMode && (
                  <p className="text-xs font-mono text-[#888888]">Documents: {filteredDocuments.length}/{documents.length}</p>
                )}
                {filteredDocuments.length === 0 ? (
                  <p className="text-[#555555] text-sm text-center py-10">Aucun document disponible.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredDocuments.map((document) => (
                      <div key={document.id} className="bg-[#111111] border border-[#2A2A2A] rounded-xl p-5">
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="min-w-0">
                            <p className="font-medium text-[#FAFAFA] truncate">{document.name}</p>
                            <p className="text-xs text-[#555555] mt-0.5">{document.type} · {formatFileSize(document.fileSize)}</p>
                            <p className="text-xs text-[#444444] mt-0.5">Ajouté le {formatDate(document.createdAt)}</p>
                          </div>
                          <a
                            href={encodeSupabaseUrl(document.fileUrl || '#')}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-1.5 border border-[#3A3A3A] text-[#AAAAAA] rounded-lg hover:border-[#D4AF37]/50 hover:text-[#D4AF37] transition-colors text-xs font-medium flex-shrink-0"
                          >
                            <Download className="w-3.5 h-3.5" />
                            Télécharger
                          </a>
                        </div>
                        {document.type === 'preview' && document.fileUrl && (
                          <div className="mt-3 rounded-lg overflow-hidden border border-[#2A2A2A]">
                            <img src={document.fileUrl} alt="Aperçu" className="w-full object-cover" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Campaign Summary */}
        <div className="fade-up fade-up-3 bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl p-6">
          <p className="text-xs text-[#555555] uppercase tracking-wider mb-5">Récapitulatif</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

            <div className="space-y-4">
              <div className="bg-[#111111] border border-[#2A2A2A] rounded-xl p-4">
                <p className="text-xs text-[#555555] uppercase tracking-wider mb-3">Détails</p>
                <div className="space-y-2.5">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#888888]">{currentInfluencerName ? 'Votre budget' : 'Budget total'}</span>
                    <span className="text-sm font-semibold text-[#FAFAFA]">{
                      currentInfluencer
                        ? formatCurrency(toNumber(currentInfluencer.rate))
                        : formatCurrency(campaign.budget || campaign.influencers?.reduce((sum, inf) => sum + toNumber(inf.rate), 0) || 0)
                    }</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#888888]">Statut</span>
                    <span className={`text-xs px-2.5 py-1 rounded-lg font-medium ${campaignStatus.badge}`}>{campaignStatus.label}</span>
                  </div>
                </div>
              </div>

              <div className="bg-[#111111] border border-[#2A2A2A] rounded-xl p-4">
                <p className="text-xs text-[#555555] uppercase tracking-wider mb-3">Étapes clés</p>
                <div className="space-y-2.5">
                  {[
                    { label: 'Devis envoyé', done: campaign.quote_sent },
                    { label: 'Contrat signé', done: campaign.contract_signed },
                    { label: 'Facture envoyée', done: campaign.invoice_sent },
                    { label: 'Paiement reçu', done: campaign.payment_received },
                  ].map(({ label, done }) => (
                    <div key={label} className="flex items-center justify-between">
                      <span className="text-sm text-[#888888]">{label}</span>
                      <span className={`w-2 h-2 rounded-full ${done ? 'bg-emerald-500' : 'bg-[#3A3A3A]'}`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-[#111111] border border-[#2A2A2A] rounded-xl p-4">
                <p className="text-xs text-[#555555] uppercase tracking-wider mb-3">Créateurs</p>
                <div className="space-y-3">
                  {campaign.influencers
                    ?.filter(inf => !currentInfluencerName || inf.fullName === currentInfluencerName)
                    .map((influencer, index) => (
                      <div key={index} className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <InfluencerAvatar profileImageUrl={influencer.profile_image_url || null} name={influencer.fullName} size="sm" />
                          <span className="text-sm text-[#FAFAFA] truncate">{influencer.fullName}</span>
                        </div>
                        <span className="text-sm font-semibold text-[#D4AF37] flex-shrink-0">{formatCurrency(influencer.rate || '0')}</span>
                      </div>
                    ))}
                </div>
              </div>

              <div className="bg-[#111111] border border-[#2A2A2A] rounded-xl p-4">
                <p className="text-xs text-[#555555] uppercase tracking-wider mb-3">Contact</p>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-[#888888]">Chef de projet</span>
                    <span className="text-sm text-[#FAFAFA]">{campaign.manager}</span>
                  </div>
                  {agencyName && (
                    <div className="flex justify-between">
                      <span className="text-sm text-[#888888]">Agence</span>
                      <span className="text-sm text-[#FAFAFA]">{agencyName}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#1E1E1E] py-6 mt-4">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span
            className="text-sm font-semibold text-[#D4AF37]"
            title={isDebugMode ? "Mode debug activé" : "Triple-cliquez pour le mode debug"}
          >
            Campaly
          </span>
          <p className="text-xs text-[#444444]">Ce lien est privé et confidentiel.</p>
        </div>
      </footer>
    </div>
  );
}